import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { SiaAiChatComponent } from '@sia/ai';
import { SiaAIFilesGridComponent } from '../ai-files-grid/ai-files-grid.component';

@Component({
  selector: 'sia-ai-assistant',
  standalone: true,
  imports: [SiaAiChatComponent, SiaAIFilesGridComponent],
  templateUrl: './ai-assistant.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiaAiAssistantComponent {
  #elementRef = inject(ElementRef);

  /** Width of the chat panel in pixels; null means use default CSS proportions */
  chatWidth = signal<number | null>(null);

  /** Whether a resize drag is in progress */
  #isResizing = signal(false);

  // --- Resize logic ---

  onResizeStart(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    this.#isResizing.set(true);
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.#isResizing()) return;
    this.#updateWidth(event.clientX);
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.#isResizing()) return;
    this.#updateWidth(event.touches[0].clientX);
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onResizeEnd(): void {
    this.#isResizing.set(false);
  }

  #updateWidth(clientX: number): void {
    const containerEl =
      this.#elementRef.nativeElement.querySelector('.flex.h-full');
    if (!containerEl) return;

    const containerRect = containerEl.getBoundingClientRect();
    const containerWidth = containerRect.width;

    // Chat is on the right: width = distance from cursor to right edge
    const newChatWidth = containerRect.right - clientX;

    const minWidth = containerWidth * 0.2;
    const maxWidth = containerWidth * 0.7;

    this.chatWidth.set(Math.max(minWidth, Math.min(maxWidth, newChatWidth)));
  }
}
